import React, { useRef, useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Checkbox,
    FormControlLabel,
    Typography
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import ApiHelper from 'utils/apiHelper';
import { convertDate, validateAlphaPlus, validateAlphanumeric } from 'utils/commonHelpers';

const EditTemplateDialog = ({ onClose, onCloseCancel, templateData, limitFields }) => {
    const fileInput = useRef(null);
    const [templateName, setTemplateName] = useState(templateData.name);
    const [description, setDescription] = useState(templateData.description);
    const [templateBody, setTemplateBody] = useState(templateData.body);
    const [updatedDate, setUpdatedDate] = useState(templateData.updated);
    const [file, setFile] = useState();
    const [fileName, setFileName] = useState(templateData.file_name);
    const [uploadFile, setUploadFile] = useState(templateData.file);
    const [loading, setLoading] = useState(false);

    const handleChangeFile = (e) => {
        setFile(e.target.files[0]);
        setFileName(e.target.files[0].name);
        setTemplateBody('');
    };

    const handleChangeCheckFile = (e) => {
        setUploadFile(e.target.checked);
        if (e.target.checked === false) {
            setFileName('');
            setFile(null);
        } else {
            setTemplateBody('');
        }
    };

    const handleChangeName = (e) => {
        if (e.target.value.length > limitFields['name']) {
            toast.warning('This field must be ' + limitFields['name'] + ' characters or less.');
        } else {
            setTemplateName(e.target.value);
        }
    };

    const handleChangeDesc = (e) => {
        if (e.target.value.length > limitFields['desc']) {
            toast.warning('This field must be ' + limitFields['desc'] + ' characters or less.');
        } else {
            setDescription(e.target.value);
        }
    };

    const handleChangeBody = (e) => {
        if (e.target.value.length > limitFields['body']) {
            toast.warning('This field must be ' + limitFields['body'] + ' characters or less.');
        } else {
            setTemplateBody(e.target.value);
        }
    };

    const handleSubmit = () => {
        if (uploadFile === false && (templateName === '' || description === '' || templateBody === '')) {
            toast.info('Please fill all values');
            return true;
        }
        if (!validateAlphanumeric(templateName)) {
            toast.info('The template name must be alphanumeric');
            return true;
        }
        if (!validateAlphaPlus(description)) {
            toast.info('The template description must be alphanumeric');
            return true;
        }
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', templateName);
        formData.append('file_name', fileName);
        formData.append('description', description);
        formData.append('body', templateBody);
        formData.append('updated', convertDate(updatedDate));
        setLoading(true);

        ApiHelper.put(`/api/management/templates/${templateData.id}/`, formData)
            .then(() => {
                toast.success('Successfully updated');
                onClose();
                setLoading(false);
            })
            .catch((err) => {
                if (err.response.data.message !== '') {
                    toast.error(`Failed! ${err.response.data.message}`);
                } else {
                    toast.error('Failed to edit the template');
                }
                setLoading(false);
            });
    };

    return (
        <Dialog open={true} onClose={onCloseCancel}>
            <DialogTitle sx={{ fontSize: '16px' }}>Edit Template</DialogTitle>
            <DialogContent sx={{ minWidth: '500px' }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Template Name"
                        value={templateName}
                        // disabled={uploadFile}
                        onChange={(e) => handleChangeName(e)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Description"
                        value={description}
                        inputProps={{
                            maxLength: 50
                        }}
                        onChange={(e) => handleChangeDesc(e)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Template Body"
                        value={templateBody}
                        disabled={uploadFile}
                        multiline={true}
                        rows={5}
                        onChange={(e) => handleChangeBody(e)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Updated Date"
                            value={updatedDate}
                            onChange={(newValue) => {
                                setUpdatedDate(newValue);
                            }}
                            renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                        />
                    </LocalizationProvider>
                </Box>
                <Divider>Or</Divider>
                <Box sx={{ mt: 3, mb: 3 }}>
                    <FormControlLabel
                        label="Upload File?"
                        control={<Checkbox checked={uploadFile} disabled={true} onChange={handleChangeCheckFile} />}
                    />
                    {uploadFile && (
                        <Box>
                            <Button color="primary" variant="contained" onClick={() => fileInput.current.click()}>
                                Choose Template File
                            </Button>
                            <input type="file" accept="text/html" ref={fileInput} style={{ display: 'none' }} onChange={handleChangeFile} />
                            <Typography sx={{ marginLeft: '15px', mt: 1 }}>{fileName}</Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseCancel}>Cancel</Button>
                <LoadingButton onClick={handleSubmit} loading={loading} color="primary" variant="contained">
                    <span>Submit</span>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

EditTemplateDialog.propTypes = {
    onClose: PropTypes.func,
    onCloseCancel: PropTypes.func,
    templateData: PropTypes.object,
    limitFields: PropTypes.object
};

export default EditTemplateDialog;
